/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, Subscription, firstValueFrom, take, tap } from 'rxjs';
import { trackingUrl } from '../../admin/admin.urls';
import { HttpClientWrapper } from '../../http-client-wrapper';

type TrackerEventDTO = {
  event_type: string;
  action_type?: string;
  page_name?: string;
  element_name?: string;
  element_type?: string;
  duration?: number;
  previous_page?: string;
  premium_feature_accessed?: boolean;
  premium_attempts?: number;
  additional_data?: JSON;
  redirect_from_signin?: boolean;
  end_time?: Date;
  device_type: string;
  timezone_offset?: number;
};
type TrackerEvent = TrackerEventDTO & {
  id: number;
};

enum TrackerEventType {
  PAGE_VISIT = 'PAGE_VISIT',
  ELEMENT_INTERACTION = 'ELEMENT_INTERACTION',
  ACTION = 'ACTION',
}

@Injectable({
  providedIn: 'root',
})
export class TrackerService {
  private hoverStartTime: number | null = null;

  private readonly _pageVisitSessionQueue: { [sessionId: string]: 'open' | 'closing' } = {};

  constructor(private http: HttpClientWrapper) {}

  // General method for tracking events
  trackEvent(eventData: TrackerEventDTO): Observable<TrackerEvent> {
    return this.http.post<TrackerEvent>(trackingUrl, eventData);
  }

  /**
   * Opens a tracking event for a page visit session.
   * Automatically requests previous event sessions to be closed (if any)
   */
  trackPageVisit(
    page_name: string,
    duration?: number,
    previous_page?: string,
    redirect_from_signin?: boolean,
    premium_feature_accessed?: boolean
  ): Observable<TrackerEvent> {
    // Close previous page visit sessions (if any)
    Object.entries(this._pageVisitSessionQueue).forEach(([sessionId, status]) => {
      if (status === 'open') this.trackPageLeave(sessionId)?.subscribe();
    });

    const eventData: TrackerEventDTO = {
      event_type: TrackerEventType.PAGE_VISIT,
      page_name,
      duration,
      previous_page,
      redirect_from_signin,
      premium_feature_accessed,
      device_type: 'Desktop',
    };
    return this.trackEvent(eventData).pipe(
      take(1),
      tap({
        next: event => {
          this._pageVisitSessionQueue[event.id.toString()] = 'open';
        },
        error: () => {},
      })
    );
  }

  /**
   * Updates page visit duration on a specified session.
   * If no session id is explicitly provided finds and closes a currently open session (if any)
   * (assume there will always be only one open page visit session)
   */
  trackPageLeave(sessionId?: string): Observable<TrackerEvent> | undefined {
    const target =
      sessionId ??
      Object.entries(this._pageVisitSessionQueue).find(([, status]) => status === 'open')?.[0];
    if (!target) return;

    this._pageVisitSessionQueue[target] = 'closing';

    return this.updateEvent(target, {
      end_time: new Date(),
      timezone_offset: new Date().getTimezoneOffset() * 60,
    }).pipe(
      take(1),
      tap({
        error: () => {
          console.error(`Failed to close page visit session for visit#"${target}"`);
        },
        finalize: () => delete this._pageVisitSessionQueue[target],
      })
    );
  }

  // Method for tracking element clicks
  trackElementClick(
    element_name: string,
    element_type: string,
    page_name: string,
    additional_data?: any
  ): Observable<any> {
    const eventData: TrackerEventDTO = {
      event_type: TrackerEventType.ELEMENT_INTERACTION,
      element_name,
      element_type,
      page_name,
      additional_data,
      device_type: 'Desktop',
    };
    return this.trackEvent(eventData);
  }

  // Method for tracking element hover
  trackElementHover(
    element_name: string,
    element_type: string,
    page_name: string,
    duration?: number,
    additional_data?: any
  ): Observable<any> {
    const eventData: TrackerEventDTO = {
      event_type: TrackerEventType.ELEMENT_INTERACTION,
      element_name,
      element_type,
      page_name,
      duration,
      additional_data,
      device_type: 'Desktop',
    };
    return this.trackEvent(eventData);
  }

  // Method for tracking specific actions
  trackAction(
    action_type: string,
    page_name: string,
    additional_data?: any,
    premium_feature_accessed?: boolean
  ): Observable<any> {
    const eventData: TrackerEventDTO = {
      event_type: TrackerEventType.ACTION,
      page_name,
      action_type,
      premium_feature_accessed,
      additional_data,
      device_type: 'Desktop',
    };
    return this.trackEvent(eventData);
  }

  /**
   * Similarly to `trackAction` records an action event,
   * but actually makes the http request instead of returning an observable 
   */
  logAction(
    action_type: string,
    page_name: string,
    additional_data?: any,
    premium_feature_accessed?: boolean
  ): Subscription {
    return this.trackAction(
      action_type,
      page_name,
      additional_data,
      premium_feature_accessed
    ).subscribe();
  }

  // Method for tracking premium feature attempts
  trackPremiumFeatureAttempt(
    element_name: string,
    premium_feature_accessed: boolean,
    premium_attempts: number,
    additional_data?: any
  ): Observable<any> {
    const eventData: TrackerEventDTO = {
      event_type: TrackerEventType.ELEMENT_INTERACTION,
      element_name,
      premium_feature_accessed,
      premium_attempts,
      additional_data,
      device_type: 'Desktop',
    };
    return this.trackEvent(eventData);
  }

  updateEvent(eventId: string, updateData: Partial<TrackerEventDTO>): Observable<any> {
    const updateUrl = `${trackingUrl}${eventId}/`;
    return this.http.patch(updateUrl, updateData).pipe();
  }

  startHover(): void {
    this.hoverStartTime = Date.now();
  }

  endHover(elementName: string, elementType: string, pageName: string): void {
    if (this.hoverStartTime) {
      const hoverEndTime = Date.now();
      const hoverDuration = (hoverEndTime - this.hoverStartTime) / 1000;
      this.hoverStartTime = null;

      if (hoverDuration >= 1) {
        void firstValueFrom(
          this.trackElementHover(elementName, elementType, pageName, hoverDuration, {
            hover: true,
          })
        );
      }
    }
  }
}
